import type { HttpHandler } from 'msw';
import type { SetupWorker } from 'msw/browser';

import { devHandlers } from './dev-handlers';

// Augment window type for MSW dev utilities
declare global {
  interface Window {
    __msw?: typeof mswDevUtils;
  }
}

let worker: SetupWorker | null = null;

/**
 * Initialize MSW for development mode.
 * Call this before React renders.
 */
export async function initMswDevMode(): Promise<void> {
  if (import.meta.env.PROD) return;
  if (import.meta.env.VITE_MSW_DEV_MODE !== 'true') return;

  try {
    const { setupWorker } = await import('msw/browser');

    // Try to load auto-generated handlers, fall back to dev handlers only
    let allHandlers: HttpHandler[] = [...devHandlers];
    try {
      const { handlers } = await import('../../mocks/generated/handlers');
      // Custom handlers first (take precedence), then auto-generated
      allHandlers = [...devHandlers, ...handlers];
    } catch {
      console.warn(
        '[MSW] Could not load generated handlers. Run `npm run codegen:mocks` to generate them.',
        'Using custom dev handlers only.',
      );
    }

    worker = setupWorker(...allHandlers);

    await worker.start({
      onUnhandledRequest: 'bypass', // Don't warn on unhandled requests
      quiet: false, // Show MSW startup message
    });

    console.log('[MSW] Development mocks enabled');
  } catch (error) {
    console.error('[MSW] Failed to initialize:', error);
    // Don't block app startup - just log the error
  }
}

/**
 * Add or override handlers at runtime (for testing specific scenarios).
 * Example: overrideMswHandlers(http.get('/api/users', () => HttpResponse.json([])))
 */
export function overrideMswHandlers(...handlers: HttpHandler[]): void {
  if (!worker) {
    console.warn('[MSW] Worker not initialized. Call initMswDevMode() first.');
    return;
  }
  worker.use(...handlers);
}

/**
 * Reset handlers to original auto-generated ones.
 */
export function resetMswHandlers(): void {
  if (!worker) return;
  worker.resetHandlers();
}

/**
 * Stop MSW worker (cleanup).
 */
export function stopMswWorker(): void {
  if (!worker) return;
  worker.stop();
  worker = null;
}

const mswDevUtils = {
  override: overrideMswHandlers,
  reset: resetMswHandlers,
  stop: stopMswWorker,
};

// Expose to window for easy console debugging
if (import.meta.env.DEV) {
  window.__msw = mswDevUtils;
}
